import { httpClient } from '@/core/api/client/httpClient'
import { isHttpError } from '@/core/api/types/http.types'
import type { LoginResult, UserProfile } from '@/core/auth/session/session.types'

interface BffLoginResponse {
  success: true
  data: {
    accessToken: string
    authProfileId: string
    contactId: string
    email: string
    firstName: string
    lastName: string
    hasLegacyProducts: boolean
    expiresAtUtc: string
  }
  errorCode: null
  correlationId: string
}

interface BffLoginErrorResponse {
  success: false
  data: unknown | null
  error?: {
    code?: string | null
    message?: string | null
    details?: { attemptsRemaining?: number } | null
  } | null
}

interface BffMeResponse {
  success: true
  data: {
    profile: {
      contactId: string
      firstName: string
      lastName: string
      email: string
    }
    expiresAtUtc: string
  }
  correlationId: string
}

export const authService = {
  async login(email: string, password: string, lang = 'es'): Promise<LoginResult> {
    try {
      const data = await httpClient.post<BffLoginResponse>(
        `/api/auth/login?lang=${lang}`,
        { email, password },
      )

      return {
        success: true,
        profile: {
          contactId: data.data.contactId,
          firstName: data.data.firstName,
          lastName: data.data.lastName,
          email: data.data.email,
        },
        expiresAtUtc: data.data.expiresAtUtc,
        accessToken: data.data.accessToken,
      }
    } catch (error) {
      if (isHttpError(error) && error.data) {
        const body = error.data as BffLoginErrorResponse
        const details = body.error?.details
        const message = body.error?.message ?? ''
        // Compatibilidad con BFFs que todavía no propagan `details` y
        // entregan los intentos en el mensaje, en español o inglés.
        const attemptsFromMessage = message.match(
          /(?:\.|\s)(\d+)\s+(?:intento|intentos|attempt|attempts)/i,
        )
        return {
          success: false,
          errorCode: body.error?.code ?? 'INTERNAL_ERROR',
          attemptsRemaining: details?.attemptsRemaining ??
            (attemptsFromMessage ? Number(attemptsFromMessage[1]) : undefined),
        }
      }

      return {
        success: false,
        errorCode: 'INTERNAL_ERROR',
      }
    }
  },

  async logout(): Promise<void> {
    try {
      await httpClient.post('/api/auth/logout')
    } catch {
      // Ignore logout errors — always clear client session.
    }
  },

  async getMe(): Promise<{ profile: UserProfile; expiresAtUtc: string } | null> {
    try {
      const data = await httpClient.get<BffMeResponse>('/api/auth/me')
      return {
        profile: data.data.profile,
        expiresAtUtc: data.data.expiresAtUtc,
      }
    } catch {
      return null
    }
  },
}
